import { useState, useMemo } from 'react';
import AdvanceTable from '../../components/base/AdvanceTable';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import Badge from '../../components/base/Badge';
import SearchBox from '../../components/common/SearchBox';
import FilterTab from '../../components/common/FilterTab';
import { Link } from 'react-router-dom';
import { ProgressBar, Spinner } from 'react-bootstrap';
import { useFinalPhaseTokens } from '../../features/jobs/hooks/useProductionDashboardStats';
import { useJobs } from '../../features/jobs/hooks/useJobs';
import { useOrders } from '../../features/orders/useOrders';
import {
  buildClientIndex,
  deriveDestination,
  summarizePalletPlan
} from '../../helpers/orderDestination';
import { machineBadgeStyle } from '../../helpers/machineColour';
import {
  productColorName,
  productColorBadgeStyle,
  colorNameFromKey
} from '../../helpers/productColor';
import dayjs from 'dayjs';
import EstimateProvider, { RowEstimate } from '../../features/machine-intervals/EstimateProvider.js';
import { formatCoreSpec } from '../../helpers/productLabel';

const PHASE_LABEL = {
  exterior_sheat: 'Exterior Sheath',
  interior_sheat: 'Interior Sheath',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  coiling: 'Coiling',
  rewinding: 'Rewinding',
  armouring: 'Armouring'
};

function statusBg(s) {
  const u = String(s || '').toUpperCase();
  if (u === 'ACTIVE' || u === 'RUNNING') return 'success';
  if (u === 'PENDING') return 'warning';
  if (u === 'COMPLETED' || u === 'DONE') return 'info';
  return 'secondary';
}

// Stable id for a row's estimate. Used by both the provider's mapper and the
// cell, so they cannot drift apart.
const estKey = r => r?.tokenId || `${r?.jobId || '?'}:${r?.phase || '?'}:${r?.plannedLength || 0}`;

const rowToEstimateToken = r => ({
  key: estKey(r),
  tokenId: r?.tokenId || null,
  machineId: r?.machineId || null,
  phase: r?.phase || null,
  productLabel: r?.productLabel || null,
  color: r?.color || null,
  plannedM: Number(r?.plannedLength) || 0,
  producedM: Number(r?.executedLength) || 0,
  started: (Number(r?.executedLength) || 0) > 0
});

const columns = [
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <Link
          to={`/apps/project-management/project-details/${original.jobId}`}
          className="text-decoration-none fw-semibold fs-9"
        >
          {original.productLabel || '—'}
        </Link>
        <span className="text-700 fs-10">
          {formatCoreSpec(original)} mm²
        </span>
      </div>
    ),
    meta: {
      cellProps: { className: 'white-space-nowrap py-2' },
      headerProps: { style: { width: '18%' } }
    }
  },
  {
    id: 'color',
    header: 'Color',
    // The finished cable's own colour — sheath for MYYM/CYYF, wire for FY.
    // Taken from the launch productKey, never from `row.color`: on one of these
    // rows that is the CONDUCTOR colour, so a white MYYM would have shown up
    // here as blue or brown.
    accessorFn: row => productColorName(row) || '',
    cell: ({ row: { original } }) => {
      const name = productColorName(original);
      if (!name) return <span className="text-body-tertiary">—</span>;
      return (
        <span
          className="badge fs-10 text-uppercase white-space-nowrap"
          style={productColorBadgeStyle(name)}
        >
          {name}
        </span>
      );
    },
    meta: {
      headerProps: { style: { width: '9%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-2' }
    }
  },
  {
    accessorKey: 'phase',
    header: 'Phase',
    cell: ({ row: { original } }) => (
      <Badge bg="primary" variant="phoenix" className="fs-10">
        {PHASE_LABEL[original.phase] || original.phase}
      </Badge>
    ),
    meta: {
      headerProps: { style: { width: '11%' } }
    }
  },
  {
    accessorKey: 'machineId',
    header: 'Machine',
    // The machine is what a planner scans this column for, and every one of them
    // wore the same grey. Now each line keeps the colour it already has on
    // Machine Monitor, so the name is recognisable before it is read.
    cell: ({ row: { original } }) =>
      original.machineId ? (
        <span
          className="badge fs-10 text-uppercase"
          style={machineBadgeStyle(original.machineId)}
        >
          {original.machineId}
        </span>
      ) : (
        <span className="text-body-tertiary">—</span>
      ),
    meta: {
      headerProps: { style: { width: '8%' } }
    }
  },
  {
    accessorKey: 'destination',
    header: 'For',
    cell: ({ row: { original } }) => {
      // Each token/drum goes EITHER to a client OR to stock — not both.
      const isClient = original.tokenIsClient;
      return (
        <div className="d-flex flex-column gap-1">
          {isClient ? (
            <Badge
              bg="info"
              variant="phoenix"
              className="fs-10 text-truncate"
              style={{ maxWidth: 160 }}
              title={original.tokenClient || 'Client'}
            >
              {original.tokenClient || 'Client'}
            </Badge>
          ) : (
            <Badge
              bg="warning"
              variant="phoenix"
              className="fs-10 rounded-pill px-2"
            >
              🏬 Stock
            </Badge>
          )}
          {isClient && original.palletSummary && (
            <span className="fs-10 text-700">📦 {original.palletSummary}</span>
          )}
          {isClient && original.tokenDue && (
            <span className="fs-10 text-700">
              Due {String(original.tokenDue).slice(0, 10)}
            </span>
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '14%' } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'Planned',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">
        {Number(original.plannedLength || 0).toLocaleString()} m
      </span>
    ),
    meta: {
      cellProps: { className: 'text-end' },
      headerProps: { style: { width: '9%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'executedLength',
    header: 'Executed',
    cell: ({ row: { original } }) => {
      const ex = Number(original.executedLength || 0);
      return (
        <span className={ex > 0 ? 'fw-semibold text-success' : 'text-700'}>
          {ex > 0 ? ex.toLocaleString() + ' m' : '—'}
        </span>
      );
    },
    meta: {
      cellProps: { className: 'text-end' },
      headerProps: { style: { width: '9%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'drum_material',
    header: 'Drum',
    cell: ({ row: { original } }) => {
      const mat = original.drum_material || '';
      const dia = original.drum_diameter || '';
      return mat ? (
        <span className="fs-10">{mat} {dia ? 'D' + dia : ''}</span>
      ) : '—';
    },
    meta: {
      headerProps: { style: { width: '8%' } }
    }
  },
  {
    id: 'estimate',
    header: 'Est. Left',
    enableSorting: false,
    cell: ({ row: { original } }) => <RowEstimate id={estKey(original)} compact />
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row: { original } }) => (
      <Badge bg={statusBg(original.status)} variant="phoenix" className="fs-10">
        {String(original.status || 'PENDING').toUpperCase()}
      </Badge>
    ),
    meta: {
      headerProps: { style: { width: '7%' } }
    }
  },
  {
    accessorKey: 'progress',
    header: 'Progress',
    cell: ({ row: { original } }) => {
      const pct = original.progress || 0;
      return (
        <div className="d-flex align-items-center gap-2">
          <ProgressBar
            now={pct}
            style={{ height: 5, flex: 1 }}
            variant={pct >= 100 ? 'success' : pct > 0 ? 'primary' : 'secondary'}
          />
          <span className="fs-10 text-700" style={{ minWidth: 28 }}>{pct}%</span>
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '10%' } }
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row: { original } }) => (
      <span className="fs-10 text-700">
        {original.createdAt ? dayjs(original.createdAt).format('DD MMM, HH:mm') : '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' } }
    }
  }
];

const ProjectDashboardTable = () => {
  const { data, isLoading, error } = useFinalPhaseTokens();
  const allRows = data?.data || [];

  // join for client/stock/pallet info (by jobId -> job.allocations -> orders)
  const { data: jobsData } = useJobs();
  const { data: ordersData } = useOrders();
  const jobMap = useMemo(() => {
    const m = {};
    (Array.isArray(jobsData) ? jobsData : []).forEach(j => {
      if (j?.jobId) m[j.jobId] = j;
    });
    return m;
  }, [jobsData]);
  const clientIndex = useMemo(
    () => buildClientIndex(ordersData || []),
    [ordersData]
  );

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  // filter tabs
  const tabItems = useMemo(() => {
    const counts = { all: allRows.length, ACTIVE: 0, PENDING: 0 };
    for (const r of allRows) {
      const s = String(r.status || '').toUpperCase();
      if (s === 'ACTIVE' || s === 'RUNNING') counts.ACTIVE++;
      else if (s === 'PENDING') counts.PENDING++;
    }
    return [
      { label: 'All', value: 'all', count: counts.all },
      { label: 'Active', value: 'ACTIVE', count: counts.ACTIVE },
      { label: 'Pending', value: 'PENDING', count: counts.PENDING }
    ];
  }, [allRows]);

  // filtered + searched rows
  const filteredRows = useMemo(() => {
    let rows = allRows;

    // status filter
    if (statusFilter === 'ACTIVE') {
      rows = rows.filter(r => {
        const s = String(r.status || '').toUpperCase();
        return s === 'ACTIVE' || s === 'RUNNING';
      });
    } else if (statusFilter === 'PENDING') {
      rows = rows.filter(r => String(r.status || '').toUpperCase() === 'PENDING');
    }

    // search
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      rows = rows.filter(r =>
        (r.productLabel || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q) ||
        (r.machineId || '').toLowerCase().includes(q) ||
        (r.phase || '').toLowerCase().includes(q) ||
        (productColorName(jobMap[r.jobId]) || colorNameFromKey(r.productKey))
          .toLowerCase()
          .includes(q) ||
        (r.jobCode || '').toLowerCase().includes(q) ||
        String(r.numberOfConductors || '').includes(q) ||
        String(r.section || '').includes(q)
      );
    }

    // sort by createdAt desc (newest first)
    rows = [...rows].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    // enrich with destination (client vs stock) + pallet plan from the order
    return rows.map(r => {
      const job = jobMap[r.jobId];
      const dest = deriveDestination(
        job?.allocations,
        job?.plannedLength ?? r.plannedLength,
        clientIndex
      );
      const palletSummary = dest.lines
        .map(l => summarizePalletPlan(l.palletPlan))
        .filter(Boolean)
        .join(' ; ');
      // Where THIS drum is going, from the most precise thing known about it to
      // the least — and there are three levels, not one.
      //
      //   1. stamped with a delivery item  → that exact customer
      //   2. stamped with an order only    → that customer; the item is chosen
      //                                      later, at the final phase
      //   3. stamped with neither          → whatever the job as a whole owes
      //
      // Only the first was consulted, so a drum on its way to a fully sold lot
      // read "Stock" all the way through rope and sheathing — the item is not
      // picked until coiling, and until then the row said the opposite of the
      // truth. Production Jobs had it right because it reads the allocations.
      const itemInfo = r.orderItemId ? clientIndex.byItem?.[r.orderItemId] : null;
      const orderInfo = r.orderId ? clientIndex.byOrder?.[r.orderId] : null;

      const jobIsAllOrders =
        dest.destination === 'CLIENT' && (dest.clients?.length || 0) > 0;

      const tokenIsClient = !!(r.orderItemId || r.orderId || jobIsAllOrders);
      const tokenClient =
        itemInfo?.client ||
        orderInfo?.client ||
        (tokenIsClient ? dest.clients?.[0] || null : null);
      const tokenDue =
        itemInfo?.dueDate ||
        orderInfo?.dueDate ||
        (tokenIsClient ? dest.dueEarliest : null);
      // dest.plannedLength is the whole JOB length — keep the TOKEN's own planned
      // length in the Planned column (each row is one final-phase token/drum).
      return {
        ...r,
        ...dest,
        plannedLength: r.plannedLength,
        // The product's colour lives on the launch key, not on the token row.
        // The join against /jobs is the better source when it lands, but that
        // list excludes COMPLETED jobs and the machine queue can still hold a
        // task for one — so the row carries its own key as the fallback.
        productColor: productColorName(job) || colorNameFromKey(r.productKey),
        palletSummary,
        tokenIsClient,
        tokenClient,
        tokenDue
      };
    });
  }, [allRows, statusFilter, searchText, jobMap, clientIndex]);

  const table = useAdvanceTable({
    data: filteredRows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" variant="primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-danger py-3 fs-9">Failed to load final phase data</div>;
  }

  return (
    <div>
      <FilterTab
        tabItems={tabItems}
        activeKey={statusFilter}
        onSelect={setStatusFilter}
        className="mb-2"
      />

      <EstimateProvider rows={filteredRows} map={rowToEstimateToken}>
      <AdvanceTableProvider {...table}>
        <div className="mb-3">
          <SearchBox
            placeholder="Search product, machine, section..."
            onChange={e => setSearchText(e.target.value || '')}
          />
        </div>

        {filteredRows.length === 0 ? (
          <div className="text-700 py-3 text-center fs-9">
            No tokens in final phase queues
          </div>
        ) : (
          <>
            <AdvanceTable
              tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
            />
            <AdvanceTableFooter pagination />
          </>
        )}
      </AdvanceTableProvider>
      </EstimateProvider>
    </div>
  );
};

export default ProjectDashboardTable;
